import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleTomorrowScheduleAtAGlance,
  type HuddleTomorrowScheduleAtAGlanceResult,
  getHuddleTomorrowScheduleAtAGlanceOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleTomorrowScheduleAppointmentsToTableRows,
  mapHuddleTomorrowScheduleSummaryToMetrics,
  type HuddleTomorrowScheduleTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleTomorrowScheduleAtAGlanceOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleTomorrowScheduleAtAGlanceResult = {
  data: HuddleTomorrowScheduleAtAGlanceResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  appointmentRows: HuddleTomorrowScheduleTableRow[]
}

function useHuddleTomorrowScheduleAtAGlanceFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleTomorrowScheduleAtAGlanceOptions & {
  fetcher: HuddleDateFetcher<HuddleTomorrowScheduleAtAGlanceResult>
}): UseHuddleTomorrowScheduleAtAGlanceResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load schedule at a glance',
  })

  const summaryMetrics = useMemo(
    () => mapHuddleTomorrowScheduleSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const appointmentRows = useMemo(
    () => mapHuddleTomorrowScheduleAppointmentsToTableRows(data?.appointments),
    [data?.appointments],
  )

  return { data, loading, error, summaryMetrics, appointmentRows }
}

export function useHuddleTomorrowScheduleAtAGlance(
  options: UseHuddleTomorrowScheduleAtAGlanceOptions,
): UseHuddleTomorrowScheduleAtAGlanceResult {
  return useHuddleTomorrowScheduleAtAGlanceFromFetcher({
    ...options,
    fetcher: getHuddleTomorrowScheduleAtAGlance,
  })
}
export function useHuddleTomorrowScheduleAtAGlanceOverview(
  options: UseHuddleTomorrowScheduleAtAGlanceOptions,
): UseHuddleTomorrowScheduleAtAGlanceResult {
  return useHuddleTomorrowScheduleAtAGlanceFromFetcher({
    ...options,
    fetcher: getHuddleTomorrowScheduleAtAGlanceOverview,
  })
}
